/*
 * WHAT A FINISHED RUN SAYS TO WHOEVER ASKED FOR IT.
 *
 * A run exists to produce an answer for its parent, and this is the sentence that answer travels in.
 * It lives here — beside `systemBlocks` and `taskAsRequest`, the other model-facing renderings —
 * because it has two consumers that must not drift: the browser, reporting a finished ROOT back into
 * the chat that started it, and the server, delivering a finished CHILD into the conversation of the
 * parent agent waiting on it. Two renderings of "what came back" is how the two halves of one tree
 * start describing it differently.
 */
import type { AgentRun } from '@vitesse/proto/vitesse/ai/v1/agent_pb.js';

/**
 * How each terminal reason reads in a sentence.
 *
 * Every one of these is a DIFFERENT thing to tell someone — "finished", "ran out of steps" and
 * "your account is empty" call for three different next actions, and collapsing them into "done"
 * is how a truncated answer gets acted on as a complete one. Keyed by the generated enum's numbers.
 */
const TERMINAL_PROSE: Record<number, string> = {
  1: 'completed',
  2: 'hit its step limit',
  3: 'hit its budget',
  5: 'was cancelled',
  6: 'was stopped',
  7: 'failed',
  8: 'ran out of credit',
  // These two read "stopped" until they were named, so a parent could not tell a run whose tool kept
  // refusing from one somebody stopped. No comma: this text sits inside the "[agent return — x, y]" head.
  9: 'produced no output',
  10: 'stopped because a tool kept failing',
};

/**
 * A finished run, as the sentence that goes back into the parent thread.
 *
 * Pure and exported so what the model is told can be asserted directly, rather than inferred from a
 * conversation that had to spawn a real agent to produce one.
 *
 * The terminal reason is stated even on success, because "completed" and "hit its step limit" are
 * the difference between an answer and a fragment — and a parent that reads a truncated result as a
 * finished one will act on it with full confidence. That is the failure this line exists to prevent.
 */
/*
 * THE LABELS, DEFINED ONCE.
 *
 * This prose is BOTH the parent model's evidence and the thing a person reads in the run record, so
 * it has two consumers with opposite needs: the model wants one flat block it can reason over, the
 * reader wants the findings, the payload and the sources told apart. The reader's view therefore has
 * to find the sections in the text.
 *
 * Parsing prose is fragile exactly when the wording is written in two places, so it is written in
 * one: `returnProse` composes with these and `parseReturn` splits on them. Rewording a section is
 * then a single edit that moves both sides together, instead of a silent failure to parse.
 */
/**
 * HOW A RETURN ANNOUNCES ITSELF — the head of every one, and the way a reader recognises one.
 *
 * A return is delivered into the parent's conversation in the USER slot, because that is the one
 * slot a running agent reads. So on the way back out — a reopened thread, an audit transcript — the
 * row has to be told apart from something the operator typed, and this prefix is the composed half
 * of that: `returnProse` writes it, `isReturnProse` recognises it, one edit moves both.
 *
 * It is NOT sufficient on its own, and deliberately not used alone: see `isReturnProse`.
 */
export const RETURN_HEAD = '[agent return — ';
export const RETURN_UNCITED = 'SOURCE NOT RETURNED:';
export const RETURN_WRONG = 'WRONG SUBJECT:';
export const RETURN_UNSOURCED = 'NOT MEASURED:';
export const RETURN_DELIVERABLE = 'Structured result:';
export const RETURN_SOURCES = 'Sources:';
export const RETURN_ASKED = 'It asked the person, and these answers are theirs:';

/** The sweep's reason for ending a tree nobody was driving (base-ui `abandonedReason`). Recognised
 *  by its opening word so the two sides need share nothing but the record. */
const isAbandonment = (summary: string | undefined): boolean => /^abandoned\b/.test(summary ?? '');

/**
 * DID THE PERSON STOP THIS RUN? — one answer, for what a return SAYS and for what the store ALLOWS.
 *
 * ABORTED is the owner's verb, and the sweep uses it too, for a tree nobody was driving; only the
 * summary tells the two apart. The return tells a parent "that is their decision", and the store
 * refuses to continue such a run. Both read this, so a parent is never told one thing while the
 * store does another. 6 is TERMINAL_REASON_ABORTED, written as the number the way TERMINAL_PROSE is.
 */
export const stoppedByPerson = (terminalReason: number, summary: string | undefined): boolean =>
  terminalReason === 6 && !isAbandonment(summary);

/**
 * What of the deliverable the PARENT is shown.
 *
 * Bounded: a deliverable is a payload, and pasting an unbounded one into the parent's context is
 * how one agent's output eats the budget for every turn that follows it. And a payload that is a
 * SOURCE — a widget module, a document — is not for the parent to read at all: it is held whole on
 * the run, and a tool that takes it by reference (a tile naming the agent) copies it from there.
 * Pasting four thousand characters of it, cut mid-line, gave the parent something it could neither
 * use nor finish, and it went looking for the rest through the child's transcript, seven times.
 * So a deliverable with a long string field is shown as its shape — the keys, the size of each long
 * one — and a small one is shown whole.
 */
const HELD_FIELD_CHARS = 1500;
export function deliverableForParent(deliverable: string): string {
  try {
    const parsed: unknown = JSON.parse(deliverable);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const entries = Object.entries(parsed as Record<string, unknown>);
      const long = entries.filter(([, v]) => typeof v === 'string' && v.length > HELD_FIELD_CHARS);
      if (long.length) {
        const shape = entries
          .map(([k, v]) =>
            typeof v === 'string' && v.length > HELD_FIELD_CHARS
              ? `${k} (${v.length} chars, held on the run)`
              : `${k}: ${JSON.stringify(v).slice(0, 200)}`,
          )
          .join('; ');
        return `held by the platform, not pasted — ${shape}. A tool that takes it by reference (name this agent) reads it whole; do not retype it.`;
      }
    }
  } catch {
    // Not JSON: shown as it is, bounded.
  }
  return deliverable.slice(0, 4000);
}

/**
 * THE AGENTS BENEATH A RUN THAT THE PERSON STOPPED — their labels, from the tree the caller holds.
 *
 * A parent whose child the person stopped did the right thing: it did not start the work again, and
 * it finished, COMPLETED, saying what it got through. Its own parent then read "completed" and had
 * no way to know a person had called part of the work off — and started the whole job again.
 * Observed live twice on 2026-11-02, a stopped widget agent bringing back a fresh dashboard
 * designer each time. Both writers of a return ask this of the tree in hand, so the line they add
 * is the same whichever writes first.
 */
export function personStoppedBelow(
  runs: readonly Pick<AgentRun, 'runId' | 'parentRunId' | 'terminalReason' | 'ret' | 'task' | 'expertId'>[],
  runId: string,
): string[] {
  const below = new Set([runId]);
  // Parents before children is not promised, so the walk repeats until nothing new joins.
  for (let grew = true; grew;) {
    grew = false;
    for (const r of runs)
      if (r.parentRunId && below.has(r.parentRunId) && !below.has(r.runId)) {
        below.add(r.runId);
        grew = true;
      }
  }
  below.delete(runId);
  return runs
    .filter((r) => below.has(r.runId) && stoppedByPerson(r.terminalReason, r.ret?.result?.summary))
    .map((r) => r.task?.label || r.expertId || 'an agent');
}

export function returnProse(run: AgentRun, stoppedBelow: readonly string[] = []): string {
  const label = run.task?.label || run.expertId || 'agent';
  const reason = TERMINAL_PROSE[run.terminalReason] ?? 'stopped';
  // Prose by contract (`AgentReturn.result.summary`) — every terminal path on this platform explains
  // itself, including the ones that stopped short. A run that ended without one is reported as
  // exactly that, rather than as an empty success.
  const summary = run.ret?.result?.summary?.trim();
  const deliverable = run.ret?.deliverableJson?.trim();
  /*
   * THE CAVEAT TRAVELS WITH THE ANSWER, in the sentence the parent actually reads.
   *
   * This prose IS the parent's evidence — it is folded into the next step's prompt, and anything not
   * in it does not exist as far as the parent's reasoning is concerned. So a figure that appears
   * nowhere in what the child received has to be named HERE, not only stored on the record where a
   * human might later look.
   *
   * Worded as the fact it is rather than as an accusation. The child may have computed the number
   * perfectly well; what the parent needs to know is that it was not read off anything, so that it
   * asks rather than forwards it as a measurement.
   */
  const unsourced = run.ret?.unsourced ?? [];
  /* Stated flatly and FIRST, because it is the stronger finding: not "this was never measured" but
   * "this contradicts what came back". A reader who sees only one line should see this one. */
  const wrong = run.ret?.misattributed ?? [];
  const invented = run.ret?.uncited ?? [];
  /* Not a finding — provenance. Last, because it qualifies the answer rather than warning about it,
   * and a reader who stops after the warnings has lost nothing. */
  const cites = run.ret?.citations ?? [];
  /*
   * WHAT THE PERSON WAS ASKED, AND SAID — right after the summary, because it is part of the answer.
   *
   * The parent cannot see the child's conversation. Without this a parent read "planned time 60
   * minutes", found nothing saying the person gave it, and told them the child had assumed both
   * figures — when the person had typed one of them a minute earlier. Placed before the first
   * marker so `parseReturn` keeps it inside the summary: a reader sees it where the answer is.
   */
  const asked = run.ret?.asked ?? [];
  /*
   * A STOP IS THE PERSON'S DECISION, and the parent is told so in as many words.
   *
   * A parent that read "was stopped" did the diligent thing: checked what the child had got
   * through, found nobody else on the job, and spawned a fresh agent with the same brief — observed
   * live, twice in a row, each new agent needing to be stopped in turn. Nothing in the return said
   * WHO stopped it. The reason enum knows (ABORTED is the owner's verb), so the return says it, and
   * says what follows from it.
   */
  const abandoned = run.terminalReason === 6 && isAbandonment(summary);
  const byPerson = stoppedByPerson(run.terminalReason, summary);
  return [
    `${RETURN_HEAD}${label}, ${reason}]`,
    summary || `It ${reason} without reporting a result.`,
    asked.length ? `\n${RETURN_ASKED}\n${asked.map((a) => `- ${a}`).join('\n')}` : '',
    byPerson
      ? "\nIt was stopped from the person's side. That is their decision: do not start another agent to finish its work and do not retry it. Report what it got through, then wait for instructions."
      : '',
    // And when the person stopped work BENEATH it — see `personStoppedBelow`. A run that was itself
    // stopped already says so above.
    stoppedBelow.length && !byPerson
      ? `\nPart of this work was stopped by the person: ${[...new Set(stoppedBelow)].join(', ')}. That is their decision: do not start it again, here or in a new agent, unless they ask.`
      : '',
    // The same verb ends an ABANDONED tree — one nothing was driving — and that is nobody's decision.
    // Told apart here, or a parent reports "you stopped it" to a person who did no such thing.
    abandoned
      ? '\nNothing was driving it — the browser session running it went away — so it was ended; the person did not stop it. Report what it got through, and start it again only if the person asks.'
      : '',
    invented.length
      ? `\n${RETURN_UNCITED} ${invented.join(', ')} — ${invented.length === 1 ? 'this link was' : 'these links were'} not in any search result this agent received. Do not treat as cited.`
      : '',
    wrong.length
      ? `\n${RETURN_WRONG} ${wrong.join('; ')} — ${wrong.length === 1 ? 'this reading contradicts' : 'these readings contradict'} what the tool actually returned. Check which it belongs to before acting on it.`
      : '',
    unsourced.length
      ? `\n${RETURN_UNSOURCED} ${unsourced.join(', ')} — ${unsourced.length === 1 ? 'this figure appears' : 'these figures appear'} nowhere in what this agent was given or received. Treat as unverified; ask before acting on it.`
      : '',
    // Bounded: a deliverable is a payload, and pasting an unbounded one into the parent's context is
    // how one agent's output eats the budget for every turn that follows it.
    deliverable && deliverable !== '{}' ? `\n${RETURN_DELIVERABLE}\n${deliverableForParent(deliverable)}` : '',
    cites.length ? `\n${RETURN_SOURCES} ${cites.join('; ')}` : '',
  ]
    .filter(Boolean)
    .join('\n');
}

/**
 * Is this text a return?
 *
 * NEVER THE WHOLE ANSWER, and a caller that uses it alone has a hole. Prose is forgeable — an
 * operator can type this prefix — so the question "did an agent write this row" is settled by the
 * record (`ConvMessage.from_run_id`, stamped by the DO and never by the sender), and this only says
 * WHICH kind of agent-written row it is: a return, as opposed to a peer's note. Authority from the
 * field, presentation from the text; getting this one wrong shows the wrong icon, and getting the
 * other one wrong would let a sentence claim the operator's authority.
 *
 * Here rather than at either call site because both the browser's fold and the console's fold ask
 * it, and two spellings of "what a return looks like" is how one surface starts drawing them as
 * something else.
 */
export function isReturnProse(text: string): boolean {
  return text.startsWith(RETURN_HEAD);
}

/** One return, taken apart for a reader. Every field may be empty; a run that just answered has
 *  only `summary`. */
export interface ParsedReturn {
  /** The bracketed line: which agent, and how it ended. */
  head: string;
  /** WHICH AGENT, on its own — so a reader can be told "reader subagent returned" in the same words
   *  its spawn was announced in, rather than shown the raw head. Empty when the head is not one this
   *  producer wrote (an older row, a hand-typed line). */
  agent: string;
  /** HOW IT ENDED, on its own: "completed", "hit its step limit", … Empty for the same reason. The
   *  distinction is load-bearing — a truncated answer read as a finished one is acted on with full
   *  confidence — so it is a field rather than something a caller re-derives from the head. */
  outcome: string;
  /** What it reported, in its own words. */
  summary: string;
  /** The findings, in the order they were written — strongest first. */
  findings: { label: string; text: string }[];
  /** The deliverable, pretty-printed when it parses and verbatim when it does not. */
  deliverable: string;
  /** Where the figures came from. */
  sources: string;
}

/**
 * Take a return apart so a reader can be shown its pieces.
 *
 * SPLITS ON THE CONSTANTS ABOVE, which is the only reason this is safe: producer and parser cannot
 * disagree about a label, because there is one label. A section that is absent yields '' rather than
 * a missing key, so a caller renders by asking whether a string is empty.
 *
 * The deliverable is RE-INDENTED rather than reformatted. `returnProse` truncates it at 4000 chars
 * to bound the parent's context, so what arrives here is frequently not valid JSON — pretty-printing
 * would throw on exactly the payloads big enough to need it. It is parsed when it can be and shown
 * verbatim when it cannot, which is what a person needs either way: never a claim about the shape of
 * something we could not read.
 */
export function parseReturn(text: string): ParsedReturn {
  const nl = text.indexOf('\n');
  const head = (nl < 0 ? text : text.slice(0, nl)).replace(/^\[|\]$/g, '').trim();
  const rest = nl < 0 ? '' : text.slice(nl + 1);
  /*
   * THE HEAD, SPLIT WHERE IT WAS COMPOSED.
   *
   * `returnProse` writes `<RETURN_HEAD><label>, <reason>]`, so the two halves come apart against the
   * same constant — one edit moves the writer and the reader together, which is the whole reason
   * that constant exists. The reason is written last and contains no comma ("hit its step limit"),
   * so the LAST separator is the boundary; a label that contains one survives intact.
   */
  const prefix = RETURN_HEAD.replace(/^\[/, '');
  const named = head.startsWith(prefix) ? head.slice(prefix.length) : '';
  const cut = named.lastIndexOf(', ');

  const marks: { at: number; label: string }[] = [];
  for (const label of [RETURN_UNCITED, RETURN_WRONG, RETURN_UNSOURCED, RETURN_DELIVERABLE, RETURN_SOURCES]) {
    const at = rest.indexOf(label);
    if (at >= 0) marks.push({ at, label });
  }
  marks.sort((a, b) => a.at - b.at);

  const sectionAt = (i: number): string => rest.slice(marks[i].at + marks[i].label.length, marks[i + 1]?.at ?? rest.length).trim();
  const out: ParsedReturn = {
    head,
    agent: cut > 0 ? named.slice(0, cut) : named,
    outcome: cut > 0 ? named.slice(cut + 2) : '',
    summary: (marks.length ? rest.slice(0, marks[0].at) : rest).trim(),
    findings: [],
    deliverable: '',
    sources: '',
  };
  for (let i = 0; i < marks.length; i++) {
    const body = sectionAt(i);
    if (marks[i].label === RETURN_DELIVERABLE) out.deliverable = reindent(body);
    else if (marks[i].label === RETURN_SOURCES) out.sources = body;
    else out.findings.push({ label: marks[i].label.replace(/:$/, ''), text: body });
  }
  return out;
}

/** Pretty-print when it parses; hand it back untouched when it does not. See `parseReturn`. */
function reindent(json: string): string {
  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    return json;
  }
}
